import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useNavigate } from "react-router-dom";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";
import "../../assets/css/datatable.css";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import {
    Eye,
    Pause,
    Play,
    Square,
    Pencil,
    Trash2,
    AlertTriangle
} from "lucide-react";
import { BellIcon } from "@heroicons/react/24/outline";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Skeleton } from "primereact/skeleton";
import WhatsAppTemplateSection from "../../components/common/WhatsAppTemplateSection";
import NotificationLinkPicker from "../../components/common/NotificationLinkPicker";

const ScheduledNotificationsPage = () => {
    const navigate = useNavigate();
    const { showToast }: any = useToast();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [actionLoading, setActionLoading] = useState<string>("");

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [totalRecords, setTotalRecords] = useState(0);

    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);

    const [showDialog, setShowDialog] = useState(false);
    const [editingNotification, setEditingNotification] = useState<any>(null);
    const [sending, setSending] = useState(false);

    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");

    const [scheduleType, setScheduleType] = useState("once");

    const [scheduledDate, setScheduledDate] = useState<any>(null);
    const [scheduledTime, setScheduledTime] = useState("");
    const [monthDate, setMonthDate] = useState(1);
    const [specificDays, setSpecificDays] = useState<string[]>([]);
    const [dayTimes, setDayTimes] = useState<{ day: string; time: string }[]>([]);

    const weekDays = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

    const [waTemplate, setWaTemplate] = useState("");
    const [waParams, setWaParams] = useState<string[]>([]);

    const [linkType, setLinkType] = useState<"none" | "post" | "profile" | "chat">("none");
    const [linkValue, setLinkValue] = useState("");

    const [selectAllUsers, setSelectAllUsers] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

    const startIndex = currentPage * itemsPerPage;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);

            const res = await apiFunctions.get("/activeusers/scheduled-notifications", {
                params: {
                    page: currentPage + 1,
                    limit: itemsPerPage,
                    search,
                    status
                }
            });

            if (res.data.success) {
                setData(res.data.data);
                setTotalRecords(res.data.totalRecords);
                settotalPages(res.data.totalPages || 1);
            }
        } catch (err) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "Failed to load scheduled notifications"
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const openEditDialog = (row: any) => {

        setEditingNotification(row);

        setTitle(row.title || "");
        setMessage(row.message || "");

        setScheduleType(row.schedule_type || "once");

        setScheduledDate(
            row.scheduled_date
                ? new Date(row.scheduled_date)
                : null
        );

        setScheduledTime(row.scheduled_time || "");

        setMonthDate(row.month_date || 1);

        setSpecificDays(row.specific_days || []);

        setDayTimes((row.day_times || []).map((dt: any) => ({ day: dt.day, time: dt.time })));

        setWaTemplate(row.whatsapp_template || "");

        setWaParams(row.whatsapp_params || []);

        setLinkType(row.link_type || "none");
        setLinkValue(row.link_value || "");

        setSelectAllUsers(
            row.target_type === "all_users"
        );

        setSelectedUsers(
            row.userids || []
        );

        setShowDialog(true);
    };

    const resetForm = () => {

        setEditingNotification(null);

        setTitle("");
        setMessage("");

        setScheduleType("once");

        setScheduledDate(null);

        setScheduledTime("");

        setMonthDate(1);

        setSpecificDays([]);

        setDayTimes([]);

        setWaTemplate("");

        setWaParams([]);

        setLinkType("none");
        setLinkValue("");

        setSelectAllUsers(false);

        setSelectedUsers([]);
    };

    const handleSaveNotification = async () => {

        try {

            setSending(true);

            const payload = {
                title,
                message,
                schedule_type: scheduleType,
                scheduled_date: scheduledDate,
                scheduled_time: scheduledTime,
                month_date: monthDate,
                specific_days: specificDays,
                day_times: dayTimes,
                whatsapp_template: waTemplate,
                whatsapp_params: waParams,
                link_type: linkType,
                link_value: linkValue.trim(),
                target_type: selectAllUsers
                    ? "all_users"
                    : "selected_users",
                userIds: selectedUsers.map(
                    (u) => u.userid || u._id || u
                )
            };

            let res;

            if (editingNotification) {

                res = await apiFunctions.put(
                    `/activeusers/scheduled-notifications/${editingNotification._id}`,
                    payload
                );

            } else {

                res = await apiFunctions.post(
                    `/activeusers/notifications`,
                    {
                        ...payload,
                        send_type: "schedule"
                    }
                );
            }

            if (res.data.success) {

                showToast({
                    severity: "success",
                    summary: "Success",
                    detail: editingNotification
                        ? "Notification updated successfully"
                        : "Notification created successfully"
                });

                setShowDialog(false);

                resetForm();

                fetchData();
            }

        } catch (err) {

            showToast({
                severity: "error",
                summary: "Error",
                detail: editingNotification
                    ? "Failed to update notification"
                    : "Failed to create notification"
            });

        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, search, status]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search, status]);

    const handlePause = async (id: string) => {
        try {

            setActionLoading(id);

            const res = await apiFunctions.put(
                `/activeusers/scheduled-notifications/${id}/pause`,
                {}
            );

            if (res.data.success) {

                showToast({
                    severity: "success",
                    summary: "Success",
                    detail: res.data.message
                });

                fetchData();
            }

        } catch (err) {

            showToast({
                severity: "error",
                summary: "Error",
                detail: "Failed to pause notification"
            });

        } finally {
            setActionLoading("");
        }
    };

    const handleResume = async (id: string) => {
        try {

            setActionLoading(id);

            const res = await apiFunctions.put(
                `/activeusers/scheduled-notifications/${id}/resume`,
                {}
            );

            if (res.data.success) {

                showToast({
                    severity: "success",
                    summary: "Success",
                    detail: res.data.message
                });

                fetchData();
            }

        } catch (err) {

            showToast({
                severity: "error",
                summary: "Error",
                detail: "Failed to resume notification"
            });

        } finally {
            setActionLoading("");
        }
    };

    const handleStop = async (id: string) => {

        confirmDialog({
            header: "Stop Notification",

            message: (
                <div className="flex items-center gap-3">
                    <AlertTriangle
                        size={22}
                        className="text-orange-500"
                    />

                    <span>
                        Are you sure you want to stop this notification?
                    </span>
                </div>
            ),

            accept: async () => {

                try {

                    setActionLoading(id);

                    const res = await apiFunctions.put(
                        `/activeusers/scheduled-notifications/${id}/stop`,
                        {}
                    );

                    if (res.data.success) {

                        showToast({
                            severity: "success",
                            summary: "Success",
                            detail: res.data.message
                        });

                        fetchData();
                    }

                } catch (err) {

                    showToast({
                        severity: "error",
                        summary: "Error",
                        detail: "Failed to stop notification"
                    });

                } finally {
                    setActionLoading("");
                }
            }
        });
    };

    const handleDelete = async (id: string) => {

        confirmDialog({
            header: "Delete Notification",

            message: (
                <div className="flex items-center gap-3">
                    <Trash2
                        size={22}
                        className="text-red-500"
                    />

                    <span>
                        Are you sure you want to delete this notification?
                    </span>
                </div>
            ),

            accept: async () => {

                try {

                    setActionLoading(id);

                    const res = await apiFunctions.delete(
                        `/activeusers/scheduled-notifications/${id}`
                    );

                    if (res.data.success) {

                        showToast({
                            severity: "success",
                            summary: "Success",
                            detail: res.data.message
                        });

                        fetchData();
                    }

                } catch (err) {

                    showToast({
                        severity: "error",
                        summary: "Error",
                        detail: "Failed to delete notification"
                    });

                } finally {
                    setActionLoading("");
                }
            }
        });
    };

    const statusBody = (row: any) => {

        let color = "bg-yellow-100 text-yellow-700";

        if (row.status === "completed") {
            color = "bg-green-100 text-green-700";
        }

        if (row.status === "running") {
            color = "bg-blue-100 text-blue-700";
        }

        if (row.status === "paused") {
            color = "bg-orange-100 text-orange-700";
        }

        if (row.status === "stopped") {
            color = "bg-red-100 text-red-700";
        }

        return (
            <span className={`px-2 py-1 rounded text-xs font-semibold ${color}`}>
                {row.status}
            </span>
        );
    };

    const typeBody = (row: any) => {
        return (
            <span className="capitalize">
                {row.is_active ? row.schedule_type : "instant"}
            </span>
        );
    };

    const actionBody = (row: any) => {
        return (
            <div className="flex flex-wrap gap-2">

                <Button
                    icon={<Eye size={20} strokeWidth={2.2} />}
                    className="w-11 h-11 flex items-center justify-center"
                    size="small"
                    severity="info"
                    tooltip="Logs"
                    onClick={() => navigate(`/admin/notification-logs/${row._id}`)}
                />

                {(row.status === "pending" || row.status === "running") && (
                    <Button
                        icon={<Pause size={20} strokeWidth={2.2} />}
                        className="w-11 h-11 flex items-center justify-center"
                        size="small"
                        severity="warning"
                        loading={actionLoading === row._id}
                        tooltip="Pause"
                        onClick={() => handlePause(row._id)}
                    />
                )}

                {row.status === "paused" && (
                    <Button
                        icon={<Play size={20} strokeWidth={2.2} />}
                        className="w-11 h-11 flex items-center justify-center"
                        size="small"
                        severity="success"
                        loading={actionLoading === row._id}
                        tooltip="Resume"
                        onClick={() => handleResume(row._id)}
                    />
                )}

                {row.status !== "completed" &&
                    row.status !== "stopped" && (
                        <Button
                            icon={<Square size={20} strokeWidth={2.2} />}
                            className="w-11 h-11 flex items-center justify-center"
                            size="small"
                            severity="danger"
                            loading={actionLoading === row._id}
                            tooltip="Stop"
                            onClick={() => handleStop(row._id)}
                        />
                    )}

                {row.status !== "completed" &&
                    row.status !== "stopped" && (
                        <Button
                            icon={<Pencil size={20} strokeWidth={2.2} />}
                            className="w-11 h-11 flex items-center justify-center"
                            size="small"
                            severity="help"
                            tooltip="Edit"
                            onClick={() => openEditDialog(row)}
                        />
                    )}

                <Button
                    icon={<Trash2 size={20} strokeWidth={2.2} />}
                    className="w-11 h-11 flex items-center justify-center"
                    size="small"
                    severity="danger"
                    outlined
                    loading={actionLoading === row._id}
                    tooltip="Delete"
                    onClick={() => handleDelete(row._id)}
                />

            </div>
        );
    };

    const header = (
        <div className="grid grid-cols-12 gap-3 items-start sm:items-center table-header mb-4">

            <div className="col-span-12 sm:col-span-6">
                <h2 className="card-title m-0 text-lg sm:text-xl">
                    Scheduled Notifications
                </h2>
            </div>

            <div className="col-span-12 sm:col-span-6 flex flex-col sm:flex-row justify-start sm:justify-end gap-2">

                <InputText
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by title or message..."
                />

                {/* <Button
                    label="Create Schedule"
                    severity="success"
                    onClick={() => {
                        resetForm();
                        setShowDialog(true);
                    }}
                /> */}

                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="border rounded px-3 py-2 min-w-[140px]"
                >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="running">Running</option>
                    <option value="completed">Completed</option>
                    <option value="resume">Resume</option>
                    <option value="paused">Paused</option>
                    <option value="stopped">Stopped</option>
                </select>

            </div>
        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    return (
        <div className="unlist-margin">
            <div>
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {loading ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : data.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <BellIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No scheduled notifications found</p>
                                </div>
                            ) : (
                                data.map((rowData: any) => (
                                    <div key={rowData._id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <div className="flex items-start justify-between gap-2 w-full">
                                            <p className="m-0 font-semibold text-sm break-words flex-1 min-w-0" style={{ color: "#264164" }}>{rowData.title}</p>
                                            {statusBody(rowData)}
                                        </div>
                                        <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                            <div><span className="font-medium">Type:</span> {typeBody(rowData)}</div>
                                            <div><span className="font-medium">Total:</span> {rowData.total_users} · <span className="font-medium">Sent:</span> {rowData.sent_count} · <span className="font-medium">Failed:</span> {rowData.failed_count}</div>
                                            <div>
                                                <span className="font-medium">Next Run:</span> {rowData.next_run_at
                                                    ? new Date(rowData.next_run_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                                                    : "-"}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap justify-end gap-2 mt-2">
                                            {actionBody(rowData)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        lazy
                        scrollable
                        scrollHeight="calc(100vh - 240px)"
                        style={{ minHeight: "calc(100vh - 165px)", background: "#fff" }}
                        header={header}
                        value={
                            loading
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({ _id: `_skeleton_${i}` }))
                                : data
                        }
                        resizableColumns
                        removableSort
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <BellIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No scheduled notifications found</p>
                            </div>
                        }
                    >
                        <Column field="title" header="Title" body={(row) => loading ? <Skeleton /> : row.title} />
                        <Column header="Type" body={(row) => loading ? <Skeleton /> : typeBody(row)} />
                        <Column header="Status" body={(row) => loading ? <Skeleton /> : statusBody(row)} />
                        <Column field="total_users" header="Total" body={(row) => loading ? <Skeleton /> : row.total_users} />
                        <Column field="sent_count" header="Sent" body={(row) => loading ? <Skeleton /> : row.sent_count} />
                        <Column field="failed_count" header="Failed" body={(row) => loading ? <Skeleton /> : row.failed_count} />
                        <Column
                            header="Next Run"
                            body={(row) =>
                                loading ? <Skeleton /> : (
                                    row.next_run_at
                                        ? new Date(row.next_run_at).toLocaleString("en-IN", {
                                            timeZone: "Asia/Kolkata"
                                        })
                                        : "-"
                                )
                            }
                        />
                        <Column header="Action" body={(row) => loading ? <Skeleton /> : actionBody(row)} />
                    </DataTable>
                )}
                <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    {/* PrimeReact Paginator */}
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                        onPageChange={(e) => {
                            setCurrentPage(e.page);
                            setItemsPerPage(e.rows);
                        }}
                    />

                    {/* Custom Pagination Buttons */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </div>
            <Dialog
                header={
                    editingNotification
                        ? "Edit Notification"
                        : "Create Notification"
                }
                visible={showDialog}
                style={{ width: "90vw", maxWidth: "500px" }}
                modal
                draggable={false}
                onHide={() => {
                    setShowDialog(false);
                    resetForm();
                }}
            >

                <div className="flex flex-col gap-3">

                    <InputTextarea
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        rows={2}
                        placeholder="Enter title"
                        className="w-full"
                    />

                    <InputTextarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        placeholder="Enter message"
                        className="w-full"
                    />

                    <Dropdown
                        value={scheduleType}
                        options={[
                            { label: "One Time", value: "once" },
                            { label: "Daily", value: "daily" },
                            { label: "Weekly", value: "weekly" },
                            { label: "Monthly", value: "monthly" },
                            { label: "Specific Days", value: "specific_days" },
                            { label: "Specific Days & Times", value: "day_time" }
                        ]}
                        onChange={(e) => setScheduleType(e.value)}
                        optionLabel="label"
                        optionValue="value"
                        className="w-full"
                    />

                    {scheduleType === "once" && (
                        <Calendar
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.value)}
                            showTime
                            hourFormat="24"
                            className="w-full"
                        />
                    )}

                    {(scheduleType === "daily" ||
                        scheduleType === "weekly" ||
                        scheduleType === "monthly" ||
                        scheduleType === "specific_days") && (
                            <input
                                type="time"
                                value={scheduledTime}
                                onChange={(e) =>
                                    setScheduledTime(e.target.value)
                                }
                                className="p-2 border rounded w-full"
                            />
                        )}

                    {scheduleType === "day_time" && (
                        <div className="flex flex-col gap-2 border rounded p-3">
                            <label className="text-sm font-medium">Select days and set time for each</label>
                            {weekDays.map(day => {
                                const entry = dayTimes.find(d => d.day === day);
                                return (
                                    <div key={day} className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={!!entry}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setDayTimes(prev => [...prev, { day, time: "09:00" }]);
                                                } else {
                                                    setDayTimes(prev => prev.filter(d => d.day !== day));
                                                }
                                            }}
                                        />
                                        <span className="capitalize text-sm" style={{ width: "90px" }}>{day}</span>
                                        {entry && (
                                            <input
                                                type="time"
                                                value={entry.time}
                                                onChange={(e) =>
                                                    setDayTimes(prev => prev.map(d =>
                                                        d.day === day ? { ...d, time: e.target.value } : d
                                                    ))
                                                }
                                                className="p-1 border rounded"
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <NotificationLinkPicker
                        linkType={linkType}
                        linkValue={linkValue}
                        onLinkTypeChange={setLinkType}
                        onLinkValueChange={setLinkValue}
                    />

                    <WhatsAppTemplateSection
                        template={waTemplate}
                        params={waParams}
                        onTemplateChange={setWaTemplate}
                        onParamsChange={setWaParams}
                    />

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={selectAllUsers}
                            onChange={(e) =>
                                setSelectAllUsers(e.target.checked)
                            }
                        />
                        <label>Send to all users</label>
                    </div>

                    <div className="flex justify-end gap-2 mt-2">

                        <Button
                            label="Cancel"
                            severity="secondary"
                            onClick={() => {
                                setShowDialog(false);
                                resetForm();
                            }}
                        />

                        <Button
                            label={
                                editingNotification
                                    ? "Update"
                                    : "Create"
                            }
                            loading={sending}
                            onClick={handleSaveNotification}
                        />

                    </div>
                </div>
            </Dialog>
            <ConfirmDialog />
        </div>
    );
};

export default ScheduledNotificationsPage;